import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Device } from '@cornerstone/shared';
import { DEVICE_CLASS_LABEL, DETECTION_STATUS_LABEL, deviceFeatureSet, effectiveDetectionStatus, type DeviceClass, type DeviceDetectionStatus } from '@cornerstone/shared';
import { cn } from '../utils/cn';

// =============================================================================
// 운영 환경(실행 환경) 표시 — 웹 브라우저 / Android APK / HIKVISION APK.
// 목록·상세·모달이 같은 배지와 같은 아이콘 버튼을 쓴다. 화면 유형(TV·세로형)과는 다른 축.
// =============================================================================

type IconName = 'refresh' | 'identify' | 'camera' | 'power' | 'restart' | 'download' | 'lock' | 'globe' | 'android' | 'panel' | 'question' | 'shield';

export function Icon({ name, size = 16, className }: { name: IconName; size?: number; className?: string }): JSX.Element {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true, className };
  switch (name) {
    case 'refresh': return <svg {...common}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>;
    case 'identify': return <svg {...common}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.5" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>;
    case 'camera': return <svg {...common}><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>;
    case 'power': return <svg {...common}><path d="M12 3v9" /><path d="M6.3 6.3a8 8 0 1 0 11.4 0" /></svg>;
    case 'restart': return <svg {...common}><path d="M3 12a9 9 0 1 0 3.2-6.9" /><path d="M3 4v5h5" /></svg>;
    case 'download': return <svg {...common}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M4 21h16" /></svg>;
    case 'lock': return <svg {...common}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;
    case 'shield': return <svg {...common}><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" /><path d="m9 12 2 2 4-4" /></svg>;
    case 'globe': return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>;
    case 'android': return <svg {...common}><rect x="6" y="3" width="12" height="18" rx="2" /><path d="M10 18h4" /></svg>;
    case 'panel': return <svg {...common}><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></svg>;
    default: return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7" /><path d="M12 17h.01" /></svg>;
  }
}

const CLASS_ICON: Record<DeviceClass, IconName> = { unknown: 'question', web_browser: 'globe', android_apk: 'android', hikvision_apk: 'panel' };
const CLASS_TONE: Record<DeviceClass, string> = {
  unknown: 'border-line text-text-dim bg-panel-2',
  web_browser: 'border-line text-text bg-panel-2',
  android_apk: 'border-green/40 text-green bg-green/10',
  hikvision_apk: 'border-accent/40 text-accent bg-accent/10',
};

export function runtimeStatus(device: Pick<Device, 'runtime'>): { deviceClass: DeviceClass; status: DeviceDetectionStatus; locked: boolean } {
  const runtime = device.runtime ?? null;
  return { deviceClass: runtime?.deviceClass ?? 'unknown', status: effectiveDetectionStatus(runtime), locked: runtime?.fingerprintLocked === true };
}

/** 배지 한 줄 — 목록에서는 compact, 상세에서는 확인 상태까지. */
export function DeviceRuntimeBadge({ device, compact = false, className }: { device: Pick<Device, 'runtime'>; compact?: boolean; className?: string }): JSX.Element {
  const { deviceClass, status, locked } = runtimeStatus(device);
  const label = DEVICE_CLASS_LABEL[deviceClass];
  const detail = locked ? '원격 제어 잠김' : status === 'confirmed' ? null : DETECTION_STATUS_LABEL[status];
  const title = locked ? '하드웨어 정보가 등록 시점과 달라 전원·무음 업데이트가 잠겼습니다.'
    : status === 'confirmed' ? `${label} · 확인됨` : status === 'stale' ? `${label} · 최근 15분 내 보고가 없습니다.`
      : status === 'provisional' ? `${label} · 제조사·모델 확인 전(APK 0.4.8 이상에서 확정)` : '아직 실행 환경 보고가 없습니다.';
  return <span title={title} className={cn('inline-flex max-w-full items-center gap-1.5 rounded border px-2 py-0.5 text-[12px] font-medium leading-5 whitespace-nowrap',
    CLASS_TONE[deviceClass], locked && 'border-red/50 text-red bg-red/10', className)}>
    <Icon name={locked ? 'lock' : CLASS_ICON[deviceClass]} size={13} />
    <span className="truncate">{label}</span>
    {!compact && detail && <span className="opacity-80">· {detail}</span>}
    {compact && (locked || status === 'provisional' || status === 'stale') && <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', locked ? 'bg-red' : 'bg-text-dimmer')} />}
  </span>;
}

/** 텍스트 버튼이 늘어나는 곳에 쓰는 정사각 아이콘 버튼. 라벨은 aria-label·title로만. */
export function IconButton({ label, icon, tone = 'default', size = 'md', className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string; icon: IconName; tone?: 'default' | 'primary' | 'danger'; size?: 'sm' | 'md';
}): JSX.Element {
  const tones = {
    default: 'border-line text-text hover:border-line-strong hover:bg-panel',
    primary: 'border-accent-solid bg-accent-solid text-white hover:bg-accent-solid-hover',
    danger: 'border-red/50 text-red hover:bg-red/10',
  };
  return <button type="button" aria-label={label} title={rest.title ?? label}
    className={cn('inline-flex items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40',
      size === 'sm' ? 'h-8 w-8' : 'h-9 w-9', tones[tone], className)} {...rest}>
    <Icon name={icon} size={size === 'sm' ? 15 : 17} />
  </button>;
}

/** 상세 카드 안의 정보 묶음 — 제목 + 라벨/값 행. 흩어진 FieldBlock 대신 한 덩어리로. */
export function InfoTile({ title, icon, trailing, rows, className, children }: {
  title: string; icon?: IconName; trailing?: ReactNode; rows?: Array<{ label: string; value: ReactNode; mono?: boolean }>; className?: string; children?: ReactNode;
}): JSX.Element {
  return <section aria-label={title} className={cn('min-w-0 rounded-md border border-line bg-panel-2/60 p-4', className)}>
    <header className="mb-3 flex items-center justify-between gap-2">
      <h3 className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-text">{icon && <Icon name={icon} size={14} className="text-text-dim" />}{title}</h3>
      {trailing}
    </header>
    {rows && <dl className="grid grid-cols-[minmax(72px,auto)_1fr] gap-x-4 gap-y-2 text-[13px]">
      {rows.map(row => <div key={row.label} className="contents">
        <dt className="text-text-dim">{row.label}</dt>
        <dd className={cn('min-w-0 break-words text-text', row.mono && 'font-mono text-[12px]')}>{row.value}</dd>
      </div>)}
    </dl>}
    {children}
  </section>;
}

export { deviceFeatureSet };
